// src/components/PricingModal.jsx
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, Sparkles } from "lucide-react";
import { getAuth } from 'firebase/auth';
import { userService } from '../services/userService';

const plans = [
  {
    "name": "Free Trial",
    "price": 0,
    "description": "Perfect for getting started.",
    "features": [
      "10 searches per day",
      "5 best-match results per search",
      "24/7 support"
    ],
  },
  {
    "name": "Pro",
    "price": 9.99,
    "description": "Most popular choice for active users.",
    "features": [
      "Unlimited searches",
      "50 best-match results per search",
      "Unlimited email generation",
      "24/7 support",
      "Customer-exclusive Community Access"
    ],
    "highlight": true
  },
  {
    "name": "Premium",
    "price": 69.99,
    "description": "Super-Matching Model with exclusive access.",
    "features": [
      "Access to QuisMatch, Exclusive Super Matching Model",
      "Unlimited searches",
      "Unlimited best-match results per search",
      "Unlimited email generation",
      "24/7 support",
      "Customer-exclusive Community Access"
    ]
  }
];

export default function PricingModal({ open, onClose }) {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const handleCheckout = async (planName) => {
    try {
      setLoading(planName);
      setError(null);

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await userService.createCheckoutSession(getPriceIdForPlan(planName));
      window.location.href = response.url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const getPriceIdForPlan = (planName) => {
    switch (planName) {
      case 'Free Trial':
        return 'price_1RhJmaHajCWqQSXMVmTs6uJM';
      case 'Pro':
        return 'price_1RhJnBHajCWqQSXMlSqZ8kWf';
      case 'Premium':
        return 'price_1RhJnOHajCWqQSXMgO0sdGoX';
      default:
        throw new Error('Invalid plan name');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Modal content */}
          <motion.div
            className="relative bg-white/80 backdrop-blur-md rounded-2xl shadow-2xl max-w-5xl w-full mx-4 overflow-hidden border border-gray-200/50"
            initial={{ scale: 0.85, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0, y: 40 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Close button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </motion.button>

            {/* Gradient background */}
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-indigo-200/50 via-white to-pink-200/50"></div>

            <div className="p-8">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-center mb-8"
              >
                <h2 className="text-3xl font-bold mb-2 text-gray-900">
                  Choose Your Plan
                </h2>
                <p className="text-gray-600">
                  Select the perfect plan for your needs and start connecting with the right people.
                </p>
              </motion.div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-center"
                >
                  {error}
                </motion.div>
              )}

              <div className="grid md:grid-cols-3 gap-6">
                {plans.map((plan, idx) => (
                  <motion.div
                    key={plan.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * idx }}
                    className={`relative flex flex-col h-full ${
                      plan.highlight ? 'md:-mt-4 md:mb-4' : ''
                    }`}
                  >
                    <div className={`flex flex-col flex-1 bg-white/60 backdrop-blur-md rounded-xl p-6 border-2 ${
                      plan.highlight 
                        ? 'border-indigo-500 shadow-xl' 
                        : 'border-gray-200/50'
                    }`}>
                      {plan.highlight && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="absolute -top-4 left-0 right-0 mx-auto w-fit"
                        >
                          <div className="bg-gradient-to-r from-indigo-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                            <Sparkles className="w-4 h-4" />
                            <span>Most Popular</span>
                          </div>
                        </motion.div>
                      )}

                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-3xl font-bold">${plan.price}</span>
                          <span className="text-gray-500">/month</span>
                        </div>
                        <p className="text-gray-600 mb-6">{plan.description}</p>
                        
                        <ul className="space-y-3 mb-6">
                          {plan.features.map((feature, index) => (
                            <motion.li
                              key={feature}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.1 * index }}
                              className="flex items-center gap-2"
                            >
                              <Check className="h-5 w-5 text-indigo-500" />
                              <span className="text-gray-700">{feature}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleCheckout(plan.name)}
                        disabled={loading === plan.name}
                        className={`w-full py-3 rounded-lg font-medium flex items-center justify-center transition-all duration-300 ${
                          plan.highlight
                            ? 'bg-gradient-to-r from-indigo-500 to-pink-500 text-white hover:from-indigo-600 hover:to-pink-600'
                            : 'bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {loading === plan.name ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          'Get Started'
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}